import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { VotingInterface } from './VotingInterface/VotingInterface';
import { Header } from './Header';
import { Toast } from './Toast/Toast';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { hasVotedInPoll, markPollAsVoted } from '../utils/anonymousId';
import type { Poll, Candidate } from '../types';

export function VotingPage() {
  const { pollId } = useParams<{ pollId: string }>();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const [pollName, setPollName] = useState('');
  const [poll, setPoll] = useState<Poll | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [currentCandidate, setCurrentCandidate] = useState<Candidate | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);

  const TRANSITION_DURATION = 400;

  useEffect(() => {
    if (poll?.settings.expiresAt) {
      const updateTimeRemaining = () => {
        const now = new Date();
        const expiresAt = new Date(poll.settings.expiresAt!);
        if (expiresAt <= now) {
          navigate(`/${pollId}/results`);
          return;
        }
        const diff = expiresAt.getTime() - now.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeRemaining(`Poll closes in ${hours}h ${minutes}m`);
      };

      updateTimeRemaining();
      const interval = setInterval(updateTimeRemaining, 60000);
      return () => clearInterval(interval);
    }
  }, [poll?.settings.expiresAt, pollId, navigate]);

  useEffect(() => {
    const fetchPoll = async () => {
      if (!pollId) return;

      try {
        setLoading(true);
        const { data: poll, error: pollError } = await supabase
          .from('polls')
          .select(`
            *,
            candidates (*)
          `)
          .eq('id', pollId)
          .single();

        if (pollError) throw pollError;
        if (!poll) throw new Error('Poll not found');

        // Check if poll is closed or expired
        if (poll.is_closed || (poll.expires_at && new Date(poll.expires_at) <= new Date())) {
          navigate(`/${pollId}/results`);
          return;
        }

        const { data: votes, error: votesError } = await supabase
          .from('votes')
          .select('*')
          .eq('poll_id', pollId)
          .eq('voter_id', userId);

        if (votesError) throw votesError;

        const voteMap: Record<string, Record<string, boolean>> = {
          [userId]: {}
        };
        
        votes?.forEach(vote => {
          voteMap[userId][vote.candidate_id] = vote.vote;
        });

        const candidates = poll.candidates;
        delete poll.candidates;

        setPoll({
          ...poll,
          candidates,
          votes: voteMap
        });
        setPollName(poll.name);
        setCandidates(candidates);
        setCurrentCandidate(candidates.length > 0 ? candidates[0] : null);
      } catch (error) {
        console.error('Error fetching poll:', error);
        if (error instanceof Error && error.message === 'Poll not found') {
          showToast('Poll not found');
        } else {
          showToast('Failed to load poll');
        }
        setTimeout(() => navigate('/'), 2000);
      } finally {
        setLoading(false);
      }
    };

    fetchPoll();
  }, [pollId, userId, navigate, showToast]);

  const moveToNextCandidate = () => {
    setIsTransitioning(true);
    setIsVisible(false);

    if (currentIndex < candidates.length - 1) {
      const nextCandidate = candidates[currentIndex + 1];
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
        setCurrentCandidate(nextCandidate);
        setIsVisible(true);
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
    } else {
      setTimeout(() => {
        if (poll) {
          markPollAsVoted(pollId!, poll.name);
          navigate(`/${pollId}/results`);
        }
      }, TRANSITION_DURATION);
    }
  };

  const handleVote = async (candidateId: string, vote: boolean) => {
    if (!poll || !userId || !pollId || isTransitioning || !currentCandidate) return;
    setIsTransitioning(true);

    try {
      // Check single upvote restriction
      if (vote && poll.settings.singleUpvoteOnly) {
        const existingUpvote = Object.entries(poll.votes[userId] || {}).find(
          ([cId, isUpvote]) => isUpvote && cId !== candidateId
        );

        if (existingUpvote) {
          const confirmChange = window.confirm(
            "Votes are restricted to a single candidate in this poll. Do you want to change your vote to this candidate?"
          );
          if (!confirmChange) {
            return;
          }

          // Change the previous upvote to a downvote
          const { error: downvoteError } = await supabase
            .from('votes')
            .upsert({
              voter_id: userId,
              candidate_id: existingUpvote[0],
              poll_id: pollId,
              vote: false
            });

          if (downvoteError) throw downvoteError;

          // Update local state
          setPoll(prev => {
            if (!prev) return null;
            const newVotes = { ...prev.votes };
            newVotes[userId][existingUpvote[0]] = false;
            return { ...prev, votes: newVotes };
          });
        }
      }

      // Save the new vote
      const { error } = await supabase
        .from('votes')
        .upsert({
          voter_id: userId,
          candidate_id: candidateId,
          poll_id: pollId,
          vote
        });

      if (error) throw error;

      // Update local state
      setPoll(prev => {
        if (!prev) return null;
        const newVotes = { ...prev.votes };
        if (!newVotes[userId]) {
          newVotes[userId] = {};
        }
        newVotes[userId][candidateId] = vote;
        return { ...prev, votes: newVotes };
      });

      moveToNextCandidate();
    } catch (error) {
      console.error('Error saving vote:', error);
      showToast('Failed to save vote. Please try again.');
      setIsTransitioning(false);
    }
  };

  const handleKeepVote = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    moveToNextCandidate();
  };

  if (loading) {
    return (
      <div className="page-transition-wrapper">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="w-full max-w-md mx-auto text-center">
            <div className="text-gray-600 dark:text-gray-400">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!poll || !candidates.length) {
    return (
      <div className="page-transition-wrapper">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="w-full max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Poll Not Available</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {poll?.settings.isClosed ? 'This poll has been closed by the creator.' :
               poll?.settings.expiresAt && new Date(poll.settings.expiresAt) <= new Date() ? 
               'This poll has expired.' : 'Poll not found.'}
            </p>
            <a 
              href="/"
              className="inline-block bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
            >
              Return Home
            </a>
          </div>
        </main>
      </div>
    );
  }

  if (poll.settings.isClosed || (poll.settings.expiresAt && new Date(poll.settings.expiresAt) <= new Date())) {
    return (
      <div className="page-transition-wrapper">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="w-full max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Poll {poll.settings.isClosed ? 'Closed' : 'Expired'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This poll is no longer accepting votes.
            </p>
            <a 
              href={`/${pollId}/results`}
              className="inline-block bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
            >
              View Results
            </a>
          </div>
        </main>
      </div>
    );
  }

  const previousVote = currentCandidate ? poll.votes[userId]?.[currentCandidate.id] : undefined;

  return (
    <div className="page-transition-wrapper">
      <Header />
      {timeRemaining && (
        <div className="text-center text-gray-600 dark:text-gray-400 py-2 bg-gray-100 dark:bg-gray-800">
          {timeRemaining}
        </div>
      )}
      <main className="container mx-auto px-4 py-8">
        <VotingInterface
          poll={poll}
          candidate={currentCandidate}
          nextCandidate={currentIndex < candidates.length - 1 ? candidates[currentIndex + 1] : null}
          currentIndex={currentIndex}
          totalCandidates={candidates.length}
          onVote={handleVote}
          onKeepVote={handleKeepVote}
          previousVote={previousVote}
          isVisible={isVisible}
        />
      </main>
      {toast && <Toast message={toast} onDismiss={hideToast} />}
    </div>
  );
}